import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

function cleanupDocsBot() {
  // Remove root element
  const chatbotRoot = document.getElementById('docsbotai-root');
  if (chatbotRoot) chatbotRoot.remove();

  // Remove all DocsBot-related elements
  document.querySelectorAll('[class*="docsbot"]').forEach(el => el.remove());
  document.querySelectorAll('.docsbotai-root').forEach(el => el.remove());
  document.querySelectorAll('[id*="docsbot"]').forEach(el => el.remove());

  // Remove DocsBot scripts
  const docsbotScripts = document.querySelectorAll('script[src*="docsbot"]');
  docsbotScripts.forEach(script => script.remove());

  // Clear global state
  if (window.DocsBotAI) {
    // Try to call shutdown/cleanup if available
    if (window.DocsBotAI.shutdown) {
      try { window.DocsBotAI.shutdown(); } catch (e) {}
    }
    window.DocsBotAI = undefined;
  }

  // Remove any iframes that might be the chatbot
  document.querySelectorAll('iframe').forEach(iframe => {
    if (iframe.src && iframe.src.includes('docsbot')) {
      iframe.remove();
    }
  });
}

export default function DocsBotChatbot() {
  const location = useLocation();
  const { user } = useAuth();
  const isOnBuilderPage = location.pathname.includes('audiencebuilder');
  const initializedRef = useRef(false);

  // Cleanup on any route change (runs before the main effect)
  useEffect(() => {
    const handleRouteChange = () => {
      if (!isOnBuilderPage) {
        cleanupDocsBot();
      }
    };

    window.addEventListener('popstate', handleRouteChange);
    return () => window.removeEventListener('popstate', handleRouteChange);
  }, [isOnBuilderPage]);

  useEffect(() => {
    // Always cleanup first
    cleanupDocsBot();

    // Only initialize on builder page with authenticated user
    if (!user || !isOnBuilderPage) {
      initializedRef.current = false;
      return;
    }

    // Avoid re-initializing
    if (initializedRef.current) {
      return;
    }
    initializedRef.current = true;

    // Initialize DocsBotAI chatbot
    const initDocsBotAI = () => {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.innerHTML = `
        window.DocsBotAI = window.DocsBotAI || {};
        DocsBotAI.init = function(e) {
          return new Promise((t, r) => {
            var n = document.createElement("script");
            n.type = "text/javascript";
            n.async = !0;
            n.src = "https://widget.docsbot.ai/chat.js";
            let o = document.getElementsByTagName("script")[0];
            o.parentNode.insertBefore(n, o);
            n.addEventListener("load", () => {
              let n;
              Promise.all([
                new Promise((t, r) => {
                  window.DocsBotAI.mount(Object.assign({}, e)).then(t).catch(r);
                }),
                (n = function e(t) {
                  return new Promise(e => {
                    if (document.querySelector(t)) return e(document.querySelector(t));
                    let r = new MutationObserver(n => {
                      if (document.querySelector(t)) return e(document.querySelector(t)), r.disconnect();
                    });
                    r.observe(document.body, { childList: !0, subtree: !0 });
                  });
                })("#docsbotai-root")
              ]).then(() => t()).catch(r);
            });
            n.addEventListener("error", e => {
              r(e.message);
            });
          });
        };
      `;
      document.head.appendChild(script);

      window.DocsBotAI.init({ id: '79beGEZbRTJjkwRrQ5wj/WiX54QtbArHzd4LmDo9h' });

      const chatbotRoot = document.createElement('div');
      chatbotRoot.id = 'docsbotai-root';
      document.body.appendChild(chatbotRoot);
    };

    initDocsBotAI();

    // Cleanup on unmount
    return () => {
      cleanupDocsBot();
      initializedRef.current = false;
    };
  }, [user, isOnBuilderPage]);

  return null;
}